import { io } from 'socket.io-client';
import { BACKEND_URL } from '../utils/FetchData';
// Create a constant for the socket server URL
// const SOCKET_URL = process.env.REACT_APP_SOCKET_URL || 'http://localhost:9007';
const SOCKET_URL = BACKEND_URL;

// Create a socket instance
let socket = null;
// Track connection status
let isConnecting = false;
// Track current room
let currentRoom = null;

const socketService = {
  // Get the socket instance
  getSocket: () => socket,

  // Get current room
  getCurrentRoom: () => currentRoom,

  // Connect to the socket server
  connectSocket: (tokenData) => {
    if (socket) {
      console.log('Socket already exists, reusing connection');
      return socket;
    }
    
    if (isConnecting) {
      console.log('Socket connection in progress, waiting...');
      return null;
    }
    
    try {
      isConnecting = true;
      // Parse the token if it's a string
      let token = tokenData;
      if (typeof tokenData === 'string' && tokenData.startsWith('{')) {
        try {
          token = JSON.parse(tokenData);
        } catch (e) {
          console.error('Failed to parse token string:', e);
        }
      }
      
      // Extract the actual token value
      const authToken = typeof token === 'object' ? token : tokenData;
      
      socket = io(SOCKET_URL, {
        transports: ['websocket'],
        auth: {
          token: authToken
        },
        reconnection: true,
        reconnectionAttempts: 5,
        reconnectionDelay: 1000,
      });

      // Log connection status
      socket.on('connect', () => {
        console.log('Socket connected:', socket.id);
        isConnecting = false;
        
        // Rejoin room if reconnecting
        if (currentRoom) {
          socket.emit('join_room', currentRoom);
          console.log('Rejoined room after reconnect:', currentRoom);
        }
        
        // Debug listener - log all incoming events
        socket.onAny((event, ...args) => {
          console.log(`[SOCKET DEBUG] Received event: ${event}`, args);
          
          // Special debugging for edit-related events
          if (event.includes('edit') || event.includes('message_update') || event.includes('admin')) {
            console.log(`[SOCKET EDIT DEBUG] Detected edit-related event: ${event}`);
            console.log('[SOCKET EDIT DEBUG] Arguments:', JSON.stringify(args));
            
            // Try to extract message ID and content for any edit-related event
            try {
              const data = args[0];
              const extractedInfo = {
                event: event,
                messageId: data?.messageId || data?.message?._id || data?._id,
                message: typeof data?.message === 'string' ? data.message : data?.message?.message,
                sender: data?.message?.sender || data?.sender,
                roomId: data?.roomId || data?.chatRoomId
              };
              console.log('[SOCKET EDIT DEBUG] Extracted info:', extractedInfo);
            } catch (e) {
              console.error('[SOCKET EDIT DEBUG] Error extracting data:', e);
            }
          }
        });
      });

      socket.on('connect_error', (err) => {
        console.error('Socket connection error:', err.message);
        isConnecting = false;
      });

      socket.on('disconnect', (reason) => {
        console.log('Socket disconnected:', reason);
        // Allow reconnection attempt if disconnected for a reason other than explicit disconnect
        if (reason !== 'io client disconnect') {
          isConnecting = false;
        }
      });
      
      return socket;
    } catch (error) {
      console.error('Error creating socket connection:', error);
      isConnecting = false;
      return null;
    }
  },

  // Disconnect from the socket server
  disconnectSocket: () => {
    if (socket) {
      // Leave the current room before disconnecting
      if (currentRoom) {
        socket.emit('leave_room', currentRoom);
        currentRoom = null;
      }
      
      socket.disconnect();
      socket = null;
      isConnecting = false;
      console.log('Socket disconnected');
    }
  },

  // Join a chat room
  joinChatRoom: (roomId) => {
    // First check if we have a socket
    if (!socket || !socket.connected) {
      console.error('Socket not connected. Cannot join room.');
      return false;
    }
    
    // If already in this room, don't do anything
    if (currentRoom === roomId) {
      console.log(`Already in room ${roomId}, no need to rejoin`);
      return true;
    }
    
    // If in a different room, leave it first
    if (currentRoom) {
      socket.emit('leave_room', currentRoom);
      console.log(`Left previous room: ${currentRoom}`);
    }
    
    // Join the new room
    socket.emit('join_room', roomId);
    currentRoom = roomId;
    console.log(`Joined room: ${roomId}`);
    return true;
  },

  // Leave a chat room
  leaveChatRoom: (roomId) => {
    // First check if we have a socket
    if (!socket || !socket.connected) {
      console.error('Socket not connected. Cannot leave room.');
      return false;
    }
    
    // Only leave if we're in this specific room
    if (currentRoom === roomId) {
      // Remove any active listeners to prevent duplicates
      socket.off('new_message');
      socket.off('user_typing');
      
      // Leave the room
      socket.emit('leave_room', roomId);
      currentRoom = null;
      console.log(`Left room: ${roomId}`);
      return true;
    }
    
    // If not in this room, just log and return success
    console.log(`Not in room ${roomId}, so no need to leave`);
    return true;
  },

  // Send a message to a chat room
  sendMessage: (data) => {
    if (!socket || !socket.connected) {
      console.error('Socket not connected. Cannot send message. Socket status:', {
        exists: !!socket,
        connected: socket?.connected,
        id: socket?.id
      });
      
      // Return failure - message will still be saved by API, but real-time delivery failed
      return false;
    }
    
    console.log('Socket sending message', {
      roomId: data.roomId,
      senderId: data.senderId,
      message: data.message,
      messageType: data.message.messageType,
      socketId: socket.id,
      connected: socket.connected
    });
    
    try {
      // Format message with consistent timestamp format (ISO string) and add socketId for tracking
      socket.emit('send_message', {
        roomId: data.roomId,
        message: {
          sender: data.senderId,
          senderName: data.senderName,
          message: data.message.message || data.message,
          fileUrl: data.message.fileUrl,
          messageType: data.message.messageType || 'text',
          // Ensure consistent timestamp format across all messages
          timestamp: new Date().toISOString(),
          chatRoomId: data.roomId,
          socketId: socket.id // Add socket ID to track message origin
        }
      });
      
      // Return success - socket is connected and message was sent
      return true;
    } catch (error) {
      console.error('Error sending message via socket:', error);
      return false;
    }
  },

  // Send typing indicator
  sendTypingIndicator: (roomId, userId, isTyping) => {
    if (socket && socket.connected) {
      socket.emit('typing', {
        roomId,
        userId,
        isTyping
      });
      return true;
    } else {
      console.error('Socket not connected. Cannot send typing indicator.');
      return false;
    }
  },

  // Edit a message
  editMessage: (data) => {
    if (!socket || !socket.connected) {
      console.error('Socket not connected. Cannot edit message. Socket status:', {
        exists: !!socket,
        connected: socket?.connected,
        id: socket?.id
      });
      
      // Return failure - message will still be updated by API, but real-time delivery failed
      return false;
    }
    
    console.log('Socket sending message edit', {
      roomId: data.roomId,
      messageId: data.messageId,
      message: data.message,
      socketId: socket.id,
      connected: socket.connected,
      preventScroll: data.preventScroll
    });
    
    try {
      // Make sure we have all the fields we need
      const messageData = {
        ...data.message,
        socketId: socket.id, // Add socket ID to track message origin
        _id: data.messageId,  // Ensure ID is included
        editedAt: data.message.editedAt || new Date().toISOString(),
        preventScroll: data.preventScroll // Pass along preventScroll flag
      };
      
      // Use the edit_message event - ensure this matches the server's expected event name
      socket.emit('edit_message', {
        roomId: data.roomId,
        messageId: data.messageId,
        message: messageData,
        preventScroll: data.preventScroll
      });
      
      // Return success - socket is connected and edit was sent
      return true;
    } catch (error) {
      console.error('Error editing message via socket:', error);
      return false;
    }
  },

  // Listen for new messages
  onNewMessage: (callback) => {
    if (socket && socket.connected) {
      // Remove previous listeners to avoid duplicates
      socket.off('new_message');
      
      // Add new listener
      socket.on('new_message', (data) => {
        console.log('Received message via socket:', {
          messageData: data,
          currentSocketId: socket.id,
        });
        
        // Handle both message structures
        let processedData = data;
        if (data.messageData) {
          processedData = {
            message: data.messageData.message,
            roomId: data.messageData.roomId || data.roomId
          };
        }
        
        // Validate the data before passing to callback
        if (processedData && (processedData.message || processedData.roomId)) {
          // Check if this message is from the current socket (shouldn't happen with socket.to())
          if (processedData.message && processedData.message.socketId === socket.id) {
            console.warn('Received own message from socket server - this should not happen!', processedData);
            return; // Don't process own messages coming back from the socket server
          }
          
          // Ensure message has an ID if it doesn't
          if (processedData.message && !processedData.message._id) {
            processedData.message._id = `socket-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
          }
          
          // Ensure timestamp is in ISO format for consistency
          if (processedData.message && processedData.message.timestamp) {
            if (typeof processedData.message.timestamp !== 'string') {
              processedData.message.timestamp = new Date(processedData.message.timestamp).toISOString();
            }
            else if (!processedData.message.timestamp.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/)) {
              processedData.message.timestamp = new Date(processedData.message.timestamp).toISOString();
            }
          } else {
            // If no timestamp, use current time
            processedData.message.timestamp = new Date().toISOString();
          }

          // Add a sequence number based on timestamp
          processedData.message.sequence = new Date(processedData.message.timestamp).getTime();
          
          callback(processedData);
        } else {
          console.error('Received invalid message data from socket:', processedData);
        }
      });
      return true;
    }
    console.error('Socket not connected. Cannot listen for messages.');
    return false;
  },

  // Listen for typing indicators
  onUserTyping: (callback) => {
    if (socket && socket.connected) {
      // Remove previous listeners to avoid duplicates
      socket.off('user_typing');
      
      // Add new listener
      socket.on('user_typing', (data) => {
        callback(data);
      });
      return true;
    } else {
      console.error('Socket not connected. Cannot listen for typing events.');
      return false;
    }
  },

  // Listen for chat settings updates
  onChatSettingsUpdated: (callback) => {
    if (socket && socket.connected) {
      // Remove previous listener to avoid duplicates
      socket.off('chat_settings_updated');
      
      // Add new listener
      socket.on('chat_settings_updated', (data) => {
        console.log('Received chat settings update via socket:', data);
        if (data && data.roomId && data.settings) {
          callback(data);
        } else {
          console.error('Received invalid chat settings data from socket:', data);
        }
      });
      return true;
    } else {
      console.error('Socket not connected. Cannot listen for chat settings updates.');
      return false;
    }
  },

  // Listen for media link submissions
  onMediaLinkSubmitted: (callback) => {
    if (socket) {
      // Remove previous listeners to avoid duplicates
      socket.off('media_link_submitted');
      
      // Add new listener
      socket.on('media_link_submitted', (data) => {
        console.log('Received media link submission via socket:', data);
        if (data && data.roomId && data.mediaType && data.link) {
          callback(data);
        } else {
          console.error('Received invalid media link data from socket:', data);
        }
      });
      return true;
    } else {
      console.error('Socket not connected. Cannot listen for media link submissions.');
      return false;
    }
  },

  // Listen for chat room order updates
  onChatRoomOrderUpdated: (callback) => {
    if (socket && socket.connected) {
      // Remove previous listeners to avoid duplicates
      socket.off('chat_room_order_updated');
      
      // Add new listener
      socket.on('chat_room_order_updated', (data) => {
        console.log('Received chat room order update:', data);
        
        // Validate the data before passing to callback
        if (data && data.userId && data.chatRoomIds) {
          callback(data);
        }
      });
      return true;
    } else {
      console.error('Socket not connected. Cannot listen for chat room order updates.');
      return false;
    }
  },

  // Listen for chat room order reset
  onChatRoomOrderReset: (callback) => {
    if (socket && socket.connected) {
      // Remove previous listeners to avoid duplicates
      socket.off('chat_room_order_reset');
      
      // Add new listener
      socket.on('chat_room_order_reset', (data) => {
        console.log('Received chat room order reset:', data);
        
        // Validate the data before passing to callback
        if (data && data.userId) {
          callback(data);
        }
      });
      return true;
    } else {
      console.error('Socket not connected. Cannot listen for chat room order reset.');
      return false;
    }
  },

  // Listen for edited messages
  onMessageEdited: (callback) => {
    if (socket && socket.connected) {
      // Remove previous listeners to avoid duplicates
      socket.off('message_edited');
      
      // Add new listener
      socket.on('message_edited', (data) => {
        console.log('Received edited message via socket:', data);
        
        // Validate the data before passing to callback
        if (data && data.messageId && data.message) {
          // Skip updates from our own edits (these are handled optimistically)
          if (data.message.socketId === socket.id) {
            console.log('Skipping our own edit event from socket');
            return;
          }
          
          callback(data);
        } else {
          console.error('Received invalid edited message data from socket:', data);
        }
      });
      return true;
    } else {
      console.error('Socket not connected. Cannot listen for edited messages.');
      return false;
    }
  },

  // Listen for admin message edits
  onAdminMessageEdited: (callback) => {
    if (socket && socket.connected) {
      // Remove previous listeners to avoid duplicates
      socket.off('admin_message_updated');
      
      // Listen specifically for the admin_message_updated event from the backend
      socket.on('admin_message_updated', (data) => {
        console.log('Received admin_message_updated event:', data);
        
        if (data && data.messageId && data.message && data.roomId) {
          callback(data);
        } else {
          console.error('Invalid data structure in admin_message_updated event:', data);
        }
      });
      
      return true;
    } else {
      console.error('Socket not connected. Cannot listen for admin message edits.');
      return false;
    }
  }
};

export default socketService; 